import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Copy, Search } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { bookingApi } from '../../api/bookingApi';
import { ClientLayout } from '../../components/layouts';
import { formatClientDate, formatClientMoney, getClientStatusMeta } from './clientHelpers';

const ClientTrackBooking = () => {
  const { trackingId } = useParams();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState(trackingId || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    bookingApi.myBookings()
      .then((response) => {
        if (cancelled) return;
        const list = response.data || [];
        setBookings(list);
        if (trackingId) {
          const localBooking = list.find((booking) => booking.trackingId === trackingId);
          if (localBooking) setSelected(localBooking);
        }
      })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || "Couldn't load your bookings."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [trackingId]);

  useEffect(() => {
    if (!trackingId) return;
    let cancelled = false;
    bookingApi.track(trackingId)
      .then((response) => { if (!cancelled) setSelected(response.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'No booking found for that tracking number.'); });
    return () => { cancelled = true; };
  }, [trackingId]);

  const recent = useMemo(() => bookings.slice(0, 6), [bookings]);

  const submitTracking = (event) => {
    event.preventDefault();
    const id = query.trim();
    if (!id) return;
    setError('');
    navigate(`/client/track/${encodeURIComponent(id)}`);
  };

  const copyTracking = async () => {
    if (!selected?.trackingId) return;
    try {
      await navigator.clipboard.writeText(selected.trackingId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (_) {}
  };

  const meta = selected ? getClientStatusMeta(selected.status) : null;

  return (
    <ClientLayout title="Track Booking">
      <div className="mx-auto w-full max-w-[1200px]">
        <div className="mb-6">
          <h2 className="m-0 font-nunito text-[clamp(1.6rem,3vw,2.15rem)] font-black text-giggs-ink">Track a booking</h2>
          <p className="mb-0 mt-2 text-sm leading-6 text-giggs-muted">Enter a tracking number or choose one of your recent bookings.</p>
        </div>

        <form onSubmit={submitTracking} className="mb-6 flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80 sm:flex-row">
          <label className="relative min-w-0 flex-1">
            <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Enter tracking number" className="box-border w-full rounded-xl border-0 bg-white py-3 pl-10 pr-4 font-mono text-sm text-giggs-ink ring-1 ring-inset ring-slate-200 outline-none focus:ring-2 focus:ring-giggs-green" />
          </label>
          <button type="submit" className="inline-flex min-h-11 cursor-pointer items-center justify-center rounded-xl border-0 bg-giggs-green px-5 py-3 text-sm font-bold text-white hover:bg-giggs-green-hover">Track booking</button>
        </form>

        {error && <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">{error}</div>}

        {selected ? (
          <section className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/80">
            <div className="flex flex-col gap-4 border-b border-slate-100 px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><h3 className="m-0 text-lg font-bold text-giggs-ink">{selected.service?.name || 'Giggs booking'}</h3>{meta && <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ring-inset ${meta.className}`}>{meta.label}</span>}</div><p className="mb-0 mt-2 text-sm text-giggs-muted">{selected.gigger?.name || 'Gigger pending'} · {formatClientDate(selected.scheduledDate)} · {selected.startTime || '—'}–{selected.endTime || '—'}</p></div>
              <span className="font-nunito text-xl font-black text-giggs-ink">{formatClientMoney(selected.total)}</span>
            </div>

            <div className="grid gap-5 px-5 py-5 sm:px-6 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="min-w-0 rounded-xl bg-slate-50 px-4 py-4"><p className="m-0 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">Tracking number</p><div className="mt-2 flex items-center gap-2"><span className="min-w-0 flex-1 break-all font-mono text-sm font-bold text-slate-700">{selected.trackingId}</span><button type="button" onClick={copyTracking} className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border-0 bg-white text-giggs-green ring-1 ring-slate-200"><Copy size={15} /></button></div>{copied && <p className="mb-0 mt-1 text-[10px] font-semibold text-giggs-green">Copied</p>}</div>
              <Link to={`/track/${selected.trackingId}`} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-giggs-dark px-5 py-3 text-sm font-bold text-white no-underline transition hover:bg-giggs-green">Open live tracking & actions <ArrowRight size={16} /></Link>
            </div>

            <div className="border-t border-slate-100 bg-slate-50/60 px-5 py-4 text-xs leading-5 text-giggs-muted sm:px-6">Live tracking includes booking progress, payment actions, cancellation, extra tasks, reviews and messaging where available.</div>
          </section>
        ) : !loading && (
          <section className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 sm:p-6">
            <h3 className="m-0 font-nunito text-lg font-bold text-giggs-ink">Recent tracking numbers</h3>
            {recent.length === 0 ? <p className="mb-0 mt-3 text-sm text-giggs-muted">You do not have any bookings yet.</p> : <div className="mt-4 grid gap-3 md:grid-cols-2">{recent.map((booking) => <Link key={booking._id} to={`/client/track/${booking.trackingId}`} className="rounded-xl border border-slate-200 bg-white px-4 py-3 no-underline transition hover:border-giggs-green/40 hover:bg-green-50/30"><p className="m-0 text-sm font-bold text-giggs-ink">{booking.service?.name || 'Giggs booking'}</p><p className="mb-0 mt-1 break-all font-mono text-[11px] text-slate-500">{booking.trackingId}</p></Link>)}</div>}
          </section>
        )}
      </div>
    </ClientLayout>
  );
};

export default ClientTrackBooking;
